import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';


type SignupShellProps = {
  children: ReactNode;
};

function useCountUp(
  target: number,
  suffix = '',
  duration = 900,
  delay = 400,
) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const timeout = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent =
          Math.round(target * eased).toLocaleString('en-IN') + suffix;
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [target, suffix, duration, delay]);

  return ref;
}

/**
 * Responsive signup shell:
 * mobile/tablet = scrollable form · laptop+ = split brand + form · TV = roomy XL
 */
export function SignupShell({ children }: SignupShellProps) {
  const launchRef = useCountUp(4, ' min');

  return (
    <div className="signup-page text-[#0F1817]">
      {/* LEFT — brand (laptop / desktop / TV) */}
      <section className="signup-left relative hidden overflow-hidden bg-[#F4F3FA] lg:flex lg:flex-col">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-[#3B66B8]/10 blur-3xl" />
          <div className="absolute right-0 bottom-10 h-72 w-72 rounded-full bg-[#0FA667]/10 blur-3xl" />
        </div>

        <div className="signup-left-inner relative z-10 flex h-full min-h-0 flex-col">
          <Link
            to="/"
            className="signup-logo-link inline-flex w-fit shrink-0 items-center self-start"
          >
            <img
              src="/screenista-logo.svg"
              alt="Screenista"
              className="signup-brand-logo block h-8 w-auto max-w-[160px] object-contain object-left xl:h-9 xl:max-w-[180px] 2xl:h-10 2xl:max-w-[200px]"
            />
          </Link>

          <div className="signup-headline shrink-0">
            <h1
              className="signup-h1 m-0 max-w-[420px] font-bold leading-[1.12] tracking-[-0.02em] text-[#2E2A54]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Advertise outdoors,
              <br />
              <span className="text-[#0FA667]">managed simply.</span>
            </h1>
            <p className="signup-sub mt-2.5 mb-0 max-w-[360px] leading-[1.55] text-[#736F8E]">
              Book screens, schedule creatives, and track reach — across one
              live network, in one place.
            </p>
          </div>

          <div className="signup-preview-card w-full max-w-[420px] shrink-0 overflow-hidden rounded-[20px] shadow-[0_14px_30px_-10px_rgba(46,96,200,0.32)] 2xl:max-w-[440px]">
            <div
              className="relative overflow-hidden px-4 py-3.5 sm:px-5 sm:py-4"
              style={{
                background:
                  'linear-gradient(135deg, #2E60C8 0%, #3659BF 35%, #76439C 75%, #823588 100%)',
              }}
            >
              <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

              <p
                className="text-[10px] font-bold tracking-[0.1em] text-white/75 uppercase"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                How it works
              </p>

              <p
                className="relative mt-2 text-[15px] font-bold leading-snug text-white xl:text-[16px]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                From blank canvas to billboard in three simple steps.
              </p>

              <div className="relative mt-3 grid grid-cols-3 gap-2 sm:gap-2.5">
                {[
                  { n: '01', t: 'Pick', d: 'Screen' },
                  { n: '02', t: 'Set', d: 'Schedule' },
                  { n: '03', t: 'Go', d: 'Live' },
                ].map((step) => (
                  <div
                    key={step.n}
                    className="rounded-xl bg-white/15 px-2.5 py-2 ring-1 ring-white/10 backdrop-blur-sm"
                  >
                    <p className="text-[9.5px] font-medium text-white/60">{step.n}</p>
                    <p
                      className="mt-0.5 text-[12px] font-bold text-white xl:text-[13px]"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {step.t}
                    </p>
                    <p className="text-[10px] text-white/75">
                      {step.d}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="signup-stats mt-2 flex w-full max-w-[420px] shrink-0 items-start justify-between gap-3 2xl:max-w-[440px]">
            <div>
              <div
                ref={launchRef}
                className="text-[20px] font-extrabold tracking-tight text-[#2E2A54] xl:text-[22px]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                250+
              </div>
              <div className="mt-0.5 text-[11px] font-medium text-[#736F8E] xl:text-[12px]">
                Screens Live
              </div>
            </div>
           
            <div>
              <div
                className="text-[20px] font-extrabold tracking-tight text-[#2E2A54] xl:text-[22px]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                25K+
              </div>
              <div className="mt-0.5 text-[11px] font-medium text-[#736F8E] xl:text-[12px]">
                Daily Impressions
              </div>
            </div>

            <div>
              <div
                className="text-[20px] font-extrabold tracking-tight text-[#2E2A54] xl:text-[22px]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                3 min
              </div>
              <div className="mt-0.5 text-[11px] font-medium text-[#736F8E] xl:text-[12px]">
                Launch Campaigns In
              </div>
            </div>
          </div>

          <div className="signup-proof mt-auto flex shrink-0 flex-wrap items-center gap-2 border-t border-[#E2E9E7] pt-3 max-w-[420px] 2xl:max-w-[440px]">
            {[
              { label: 'Verified inventory', icon: 'check' as const },
              { label: 'Live delivery', icon: 'pulse' as const },
              { label: 'Per-second billing', icon: 'meter' as const },
            ].map((item) => (
              <div
                key={item.label}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E9E7] bg-white px-2.5 py-1.5"
              >
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#DBF2EC] text-[#0D8A73]">
                  {item.icon === 'check' ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M1.5 5.2L3.8 7.5L8.5 2.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                  {item.icon === 'pulse' ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M1 5h1.6l1-2.5L5.2 7.5 6.6 5H9"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                  {item.icon === 'meter' ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <circle
                        cx="5"
                        cy="5"
                        r="3.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                      />
                      <path
                        d="M5 5L7 3.2"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : null}
                </span>
                <span className="text-[11px] font-medium text-[#3E524F] xl:text-[11.5px]">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIGHT — form (all breakpoints) */}
      <section className="signup-right min-h-0 overflow-y-auto overscroll-contain bg-[#F3F2F9]">
        <div className="signup-right-scroll flex min-h-full w-full flex-col justify-center">
        <div className="signup-right-inner mx-auto w-full min-w-0">
          <Link
            to="/"
            className="mb-5 inline-flex shrink-0 items-center self-start lg:hidden"
          >
            <img
              src="/screenista-logo.svg"
              alt="Screenista"
              className="block h-8 w-auto max-w-[150px] object-contain object-left sm:h-9 sm:max-w-[168px]"
            />
          </Link>
          {children}
        </div>
        </div>
      </section>
    </div>
  );
}
